import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * SalesResultsPage - Sales document processing results display and confirmation
 * Shows the results of AI-powered sales document scanning with editing capabilities
 * Features: Edit/Preview mode toggle, inline editing, validation, confirmation flow
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Icon } from '../components/ui/Icon';
import { FormField } from '../components/forms/FormField';
import { ConfirmModal } from '../components/ui/ConfirmModal';

import {
  CoversVolume,
  CoversVolumeUpdate,
  SalesDocumentConfirmation,
} from '../types/api';
import { queryKeys } from '../hooks/useApi';
import { validationSchemas } from '../components/forms/validation';
import { formatDate, formatIsoDateForInput } from '../utils/formatters';
import { createSalesLogger } from '../utils/logger';
import apiService from '../services/api';
import styled from 'styled-components';
import * as yup from 'yup';

// ============================================================================
// Utility Functions
// ============================================================================
// Note: Date formatting utilities moved to ../utils/formatters.ts for consistency

// ============================================================================
// Types
// ============================================================================

type ViewMode = 'preview' | 'edit';

// Derive the form type directly from the validation schema
type SalesFormData = yup.InferType<typeof validationSchemas.salesDocumentEdit>;

// ============================================================================
// Styled Components
// ============================================================================

const ResultsContainer = styled.div`
  width: 100%;
  max-width: 56rem;
  min-width: 0;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const ResultsGrid = styled.div`
  display: grid;
  gap: var(--space-xl);
  margin-bottom: var(--space-2xl);
`;

const SalesCard = styled(Card)`
  padding: var(--space-xl);
`;

const SalesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--border);
`;

const SalesTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const StatusBadge = styled.span`
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  background: var(--brand-100);
  color: var(--brand-700);
`;

const EditFormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 360px) {
    gap: var(--space-sm);
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const SectionTitle = styled.h3`
  margin: 0 0 var(--space-md) 0;
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const SaveCancelActions = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-top: var(--space-xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--border);
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 360px) {
    gap: var(--space-sm);
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const FieldLabel = styled.label`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: none;
  letter-spacing: 0.5px;
`;

const FieldValue = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  word-break: break-word;
`;

const ActionsSection = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-top: var(--space-xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--border);
`;

const BackButton = styled(Button)`
  min-width: 120px;
`;

const EditButton = styled(Button)`
  min-width: 120px;
`;

const ConfirmButton = styled(Button)`
  min-width: 120px;
`;

const DeleteButton = styled(Button)`
  background-color: var(--danger-600);
  border-color: var(--danger-600);
  color: white;

  &:hover:not(:disabled) {
    background-color: var(--danger-600, #b91c1c);
    border-color: var(--danger-600, #b91c1c);
  }
`;

// ============================================================================
// Component
// ============================================================================

export const SalesResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: volumeId } = useParams<{ id: string }>();

  // Initialize logger with correlation ID for this session
  const salesLogger = createSalesLogger();

  // Component state
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [isConfirming, setIsConfirming] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load covers volume data from database (similar to invoice results pattern)
  const {
    data: coversVolume,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['covers-volume', volumeId],
    queryFn: async (): Promise<CoversVolume> => {
      if (!volumeId) throw new Error('Volume ID is required');
      return apiService.getCoversVolume(Number(volumeId));
    },
    enabled: !!volumeId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // For backward compatibility, we'll call it 'parsedData' in the rest of the component
  const parsedData = coversVolume
    ? {
        period_start: coversVolume.period_start,
        period_end: coversVolume.period_end,
        covers_count: coversVolume.covers_count,
        source: coversVolume.source,
        category: coversVolume.category,
        shift: coversVolume.shift,
        corrected_fields: coversVolume.corrected_fields,
        filename: coversVolume.filename || `sales-document-${volumeId}`,
      }
    : null;

  // Form setup with proper default values
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<SalesFormData>({
    resolver: yupResolver(validationSchemas.salesDocumentEdit) as any,
    mode: 'onBlur',
    defaultValues: {
      period_start: '',
      period_end: '',
      covers_count: 0,
      category: null,
      shift: null,
    },
  });

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiService.deleteCoversVolume(Number(id));
    },
    onSuccess: () => {
      toast.success('Documento eliminado exitosamente');

      salesLogger.info('Sales document deleted successfully', {
        operation: 'deleteSalesDocument',
        volumeId,
      });

      // Batch invalidate all related queries for better performance
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['unified-transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['covers-volumes'] }),
        queryClient.refetchQueries({ queryKey: ['dashboard', 'summary'] }),
        queryClient.invalidateQueries({ queryKey: ['analytics'] }),
      ]).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[SalesResultsPage] Some query invalidations failed:',
            error,
          );
        }
      });

      navigate('/transactions');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al eliminar el documento');
      setShowDeleteModal(false);
    },
  });

  // Initialize form when data first loads - single source of truth
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    if (parsedData && !isLoading && !isFormInitialized) {
      // Initialize form data once when data loads
      reset({
        period_start: formatIsoDateForInput(parsedData.period_start),
        period_end: formatIsoDateForInput(parsedData.period_end),
        covers_count: parsedData.covers_count ?? 0,
        category: parsedData.category || null,
        shift: parsedData.shift || null,
      });
      setIsFormInitialized(true);
    }
  }, [parsedData, reset, isLoading, isFormInitialized]);

  // Validation for required parameters
  useEffect(() => {
    if (!volumeId) {
      toast.error('No se proporcionó un ID de documento de ventas válido');
      navigate('/data-entry?tab=sales-data');
    }
  }, [volumeId, navigate]);

  // Handle navigation and actions
  const handleBack = useCallback(() => {
    if (viewMode === 'edit' && isDirty) {
      toast.error('Por favor, guarda o cancela los cambios antes de salir.');
    } else {
      salesLogger.debug('User navigating back to transactions page', {
        operation: 'handleBack',
        volumeId,
        viewMode,
      });
      navigate('/transactions');
    }
  }, [viewMode, isDirty, navigate]);

  const handleModeToggle = useCallback((mode: ViewMode) => {
    // Simply toggle view mode - form data is already initialized
    setViewMode(mode);
  }, []);

  const handleCancel = useCallback(() => {
    if (parsedData) {
      reset({
        period_start: formatIsoDateForInput(parsedData.period_start),
        period_end: formatIsoDateForInput(parsedData.period_end),
        covers_count: parsedData.covers_count,
        category: parsedData.category || null,
        shift: parsedData.shift || null,
      });
    }
    setViewMode('preview');
  }, [parsedData, reset]);

  const handleDelete = useCallback(() => {
    if (volumeId) {
      deleteMutation.mutate(volumeId);
    }
  }, [volumeId]);

  // Update mutation for sales document
  const updateSalesDocumentMutation = useMutation({
    mutationFn: async (formData: SalesFormData) => {
      if (!volumeId) throw new Error('Volume ID is required');

      const baseConfirmationData: Pick<
        SalesDocumentConfirmation,
        | 'period_start'
        | 'period_end'
        | 'covers_count'
        | 'source'
        | 'category'
        | 'shift'
        | 'corrected_fields'
      > = {
        period_start: formData.period_start,
        period_end: formData.period_end,
        covers_count: formData.covers_count,
        source: parsedData?.source || 'ocr',
        category: formData.category || null,
        shift: formData.shift || null,
        corrected_fields: parsedData?.corrected_fields ?? {},
      };

      const confirmationData: SalesDocumentConfirmation = {
        ...baseConfirmationData,
        ...(parsedData?.filename !== undefined
          ? { filename: parsedData.filename }
          : {}),
      };

      const payload: CoversVolumeUpdate = {
        period_start: confirmationData.period_start,
        period_end: confirmationData.period_end,
        covers_count: confirmationData.covers_count,
        source: confirmationData.source,
        category: confirmationData.category ?? null,
        shift: confirmationData.shift ?? null,
        corrected_fields: confirmationData.corrected_fields ?? null,
      };

      return apiService.updateCoversVolume(Number(volumeId), payload);
    },
    onSuccess: async (_updatedData) => {
      toast.success('¡Documento de ventas actualizado exitosamente!');

      salesLogger.info('Sales document updated successfully', {
        operation: 'updateSalesDocument',
        volumeId,
      });

      // Batch invalidate all related queries for better performance
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['covers-volume', volumeId],
          }),
          queryClient.invalidateQueries({ queryKey: ['covers-volumes'] }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.unifiedTransactions.all,
          }),
          queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary }),
          queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all }),
        ]);
      } catch (error) {
        salesLogger.warn(
          'Some query invalidations failed after update',
          {
            operation: 'updateSalesDocument',
            volumeId,
          },
          { error },
        );
      }

      salesLogger.debug(
        'Navigating to transactions page after successful update',
        {
          operation: 'updateSalesDocument',
          volumeId,
        },
      );
      // Navigate back to transactions page
      navigate('/transactions');
    },
    onError: (error: any) => {
      salesLogger.error('Failed to update sales document', error, {
        operation: 'updateSalesDocument',
        volumeId,
      });
      toast.error(
        error.message ||
          'Error al actualizar el documento de ventas. Inténtalo de nuevo.',
      );
    },
  });

  // Handle form submission - update the existing data
  const handleConfirm = useCallback(
    async (formData: SalesFormData) => {
      if (!parsedData || !volumeId) return;

      setIsConfirming(true);
      try {
        await updateSalesDocumentMutation.mutateAsync(formData);
      } finally {
        setIsConfirming(false);
      }
    },
    [parsedData, volumeId, updateSalesDocumentMutation],
  );

  // Loading state
  if (isLoading) {
    return (
      <ResultsContainer>
        <LoadingSpinner message="Cargando datos del documento de ventas..." />
      </ResultsContainer>
    );
  }

  // Error state
  if (error || !parsedData) {
    salesLogger.error('Failed to load covers volume data', error || undefined, {
      operation: 'loadCoversVolume',
      volumeId,
    });
    return (
      <ResultsContainer>
        <Header>
          <Title>Error</Title>
          <Subtitle>
            No se pudo cargar el documento de ventas solicitado.
          </Subtitle>
        </Header>
        <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-lg)' }}>
            ❌
          </div>
          <p
            style={{
              color: 'var(--text-muted)',
              marginBottom: 'var(--space-xl)',
            }}
          >
            {error?.message ||
              'El documento de ventas no existe o no tienes permisos para verlo.'}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/data-entry?tab=sales-data')}
          >
            Volver a Datos de Ventas
          </Button>
        </div>
      </ResultsContainer>
    );
  }

  // Render preview mode
  const renderPreviewMode = () => (
    <>
      <SalesHeader>
        <SalesTitle>
          <Icon name="file" size="sm" /> Datos Extraídos del Documento
        </SalesTitle>
        <StatusBadge>OCR Procesado</StatusBadge>
      </SalesHeader>

      <PreviewGrid>
        <FieldGroup>
          <FieldLabel>Período de Inicio</FieldLabel>
          <FieldValue>{formatDate(parsedData.period_start)}</FieldValue>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Período de Fin</FieldLabel>
          <FieldValue>{formatDate(parsedData.period_end)}</FieldValue>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Número de Cubiertos</FieldLabel>
          <FieldValue>{parsedData.covers_count.toLocaleString()}</FieldValue>
        </FieldGroup>

        {parsedData.category && (
          <FieldGroup>
            <FieldLabel>Categoría</FieldLabel>
            <FieldValue>{parsedData.category}</FieldValue>
          </FieldGroup>
        )}

        {parsedData.shift && (
          <FieldGroup>
            <FieldLabel>Turno</FieldLabel>
            <FieldValue>{parsedData.shift}</FieldValue>
          </FieldGroup>
        )}

        {parsedData.filename && (
          <FieldGroup>
            <FieldLabel>Archivo Original</FieldLabel>
            <FieldValue>{parsedData.filename}</FieldValue>
          </FieldGroup>
        )}
      </PreviewGrid>

      <ActionsSection>
        <BackButton variant="secondary" onClick={handleBack}>
          <Icon name="arrow-left" size="xs" /> Volver
        </BackButton>
        <EditButton
          variant="secondary"
          onClick={() => handleModeToggle('edit')}
        >
          <Icon name="edit" size="xs" /> Editar
        </EditButton>
        <DeleteButton
          variant="secondary"
          onClick={() => setShowDeleteModal(true)}
        >
          <Icon name="delete" size="xs" /> Eliminar
        </DeleteButton>
        <ConfirmButton
          variant="primary"
          onClick={() =>
            handleConfirm({
              period_start: formatIsoDateForInput(parsedData.period_start),
              period_end: formatIsoDateForInput(parsedData.period_end),
              covers_count: parsedData.covers_count,
              category: parsedData.category || null,
              shift: parsedData.shift || null,
            })
          }
          loading={isConfirming}
        >
          <Icon name="confirm" size="xs" />
          {isConfirming ? 'Confirmando...' : 'Confirmar y Guardar'}
        </ConfirmButton>
      </ActionsSection>
    </>
  );

  // Render edit mode
  const renderEditMode = () => {
    // Safety check: ensure we have data before rendering the form
    if (!parsedData) {
      return (
        <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
          <LoadingSpinner message="Cargando datos para edición..." />
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit(handleConfirm)}>
        <SalesHeader>
          <SalesTitle>
            <Icon name="edit" size="sm" /> Editar Datos del Documento
          </SalesTitle>
          <StatusBadge>Modo Edición</StatusBadge>
        </SalesHeader>

        <EditFormContainer>
          <FormSection>
            <SectionTitle>
              <Icon name="calendar" size="sm" /> Período
            </SectionTitle>

            <FormField
              label="Fecha de Inicio"
              name="period_start"
              type="date"
              register={register}
              error={errors.period_start?.message}
              required
            />

            <FormField
              label="Fecha de Fin"
              name="period_end"
              type="date"
              register={register}
              error={errors.period_end?.message}
              required
            />
          </FormSection>

          <FormSection>
            <SectionTitle>
              <Icon name="users" size="sm" /> Datos de Servicio
            </SectionTitle>

            <FormField
              label="Número de Cubiertos"
              name="covers_count"
              type="number"
              register={register}
              error={errors.covers_count?.message}
              required
            />

            <FormField
              label="Categoría (Opcional)"
              name="category"
              type="text"
              register={register}
              error={errors.category?.message}
              placeholder="Ej: Desayuno, Almuerzo, Cena"
            />

            <FormField
              label="Turno (Opcional)"
              name="shift"
              type="text"
              register={register}
              error={errors.shift?.message}
              placeholder="Ej: Mañana, Tarde, Noche"
            />
          </FormSection>
        </EditFormContainer>

        <SaveCancelActions>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isConfirming}
          >
            <Icon name="close" size="xs" /> Cancelar
          </Button>
          <DeleteButton
            type="button"
            variant="secondary"
            onClick={() => setShowDeleteModal(true)}
            disabled={isConfirming}
          >
            <Icon name="delete" size="xs" /> Eliminar
          </DeleteButton>
          <Button
            type="submit"
            variant="primary"
            loading={isConfirming}
            disabled={isConfirming}
          >
            <Icon name="confirm" size="xs" />
            {isConfirming ? 'Confirmando...' : 'Confirmar y Guardar'}
          </Button>
        </SaveCancelActions>
      </form>
    );
  };

  return (
    <ResultsContainer>
      <Header>
        <Title>Revisar Documento de Ventas</Title>
        <Subtitle>
          Verifica que los datos extraídos sean correctos antes de guardarlos.
        </Subtitle>
      </Header>

      <ResultsGrid>
        <SalesCard>
          {viewMode === 'preview' ? renderPreviewMode() : renderEditMode()}
        </SalesCard>
      </ResultsGrid>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Eliminar Documento de Ventas"
        message="¿Estás seguro de que quieres eliminar este documento de ventas? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={deleteMutation.isPending}
        variant="danger"
      />
    </ResultsContainer>
  );
};

SalesResultsPage.displayName = 'SalesResultsPage';
